import { useDvFlag, useDvFlagAccess } from "./featureFlag";
import flagService from "./featureFlag/flagService.js";
import hazardReportingService from "./hazardReportingService.js";

/**
 * Gets the navigation links, including conditional items based on feature flags
 * Note: This function may be called before flags are loaded, so we handle that gracefully
 * @returns {Array} Array of navigation link objects
 */
export function getNavbarLinks() {
  const baseLinks = [
    {
      label: "navigation.realtime",
      icon: "i-material-symbols-dashboard",
      to: useDvFlagAccess("beepiz-dashboard-live-session-can:read")
        ? "realtime"
        : "realtime_denied",
    },

    {
      label: "navigation.kpi",
      icon: "i-material-symbols-bar-chart",
      to: useDvFlagAccess("beepiz-kpi-can:read")
        ? "/redirect/kpi"
        : "kpi_denied",
    },
    {
      label: "navigation.reports",
      icon: "i-material-symbols-note",
      to: useDvFlagAccess("beepiz-report-can:read")
        ? "reports"
        : "reports_denied",
    },
    {
      label: "navigation.apps",
      icon: "i-material-symbols-smartphone",
      children: [
        {
          label: "navigation.devices",
          to: useDvFlagAccess("beepiz-licence-can:read")
            ? "devices"
            : "devices_denied",
        },
        {
          label: "navigation.groups",
          to: useDvFlagAccess("beepiz-group-can:read")
            ? "groups"
            : "groups_denied",
        },
        {
          label: "navigation.licenses",
          to: useDvFlagAccess("beepiz-licence-unassociated-can:read")
            ? "licenses"
            : "licenses_denied",
        },
      ],
    },
    {
      label: "navigation.configuration",
      icon: "i-material-symbols-manufacturing",
      children: [
        {
          label: "navigation.watchmen",
          to: useDvFlagAccess("beepiz-watchmen-can:read")
            ? "/redirect/users"
            : "watchmen_denied",
        },
        {
          label: "navigation.profiles",
          to: useDvFlagAccess("beepiz-profile-can:read")
            ? "profiles"
            : "profiles_denied",
        },
        {
          label: "navigation.first_aid_worker",
          to: useDvFlagAccess("beepiz-watchmen-can:read")
            ? "/redirect/first-aid-worker"
            : "watchmen_denied",
        },
        {
          label: "navigation.remotemonitoring",
          to: useDvFlagAccess("beepiz-remote-monitoring-can:read")
            ? "remotemonitoring"
            : "remotemonitoring_denied",
        },
      ],
    },
    {
      label: "navigation.parameters",
      icon: "i-material-symbols-settings",
      children: [
        {
          label: "navigation.phonebook",
          to: useDvFlagAccess("beepiz-phonebook-can:read")
            ? "phonebook"
            : "phonebook_denied",
        },
        {
          label: "navigation.notification",
          to: useDvFlagAccess("beepiz-notification-can:read")
            ? "notification"
            : "notification_denied",
        },
        {
          label: "navigation.planning",
          to: useDvFlagAccess("beepiz-planning-reminder-can:read")
            ? "planning"
            : "planning_denied",
        },
        {
          label: "navigation.ppes",
          to: useDvFlagAccess("beepiz-ppe-list-can:read")
            ? "ppes"
            : "ppes_denied",
        },
        {
          label: "navigation.tos",
          to: useDvFlagAccess("beepiz-cgu-can:read") ? "tos" : "tos_denied",
        },
        {
          label: "navigation.identifications",
          to: useDvFlagAccess("beepiz-identifications-can:read")
            ? "identifications"
            : "identifications_denied",
        },
      ],
    },
    {
      label: "navigation.patrols",
      icon: "i-material-symbols-route",
      children: [
        {
          label: "navigation.patrols_recent",
          to: useDvFlagAccess("beepiz-patrols-lastest-can:read")
            ? "patrols_recent"
            : "patrols_recent_denied",
        },
        {
          label: "navigation.patrols_list",
          to: useDvFlagAccess("beepiz-patrols-can:read")
            ? "patrols_list"
            : "patrols_list_denied",
        },
        {
          label: "navigation.patrols_compare",
          to: useDvFlagAccess("beepiz-patrols-can:read")
            ? "patrols_compare"
            : "patrols_compare_denied",
        },
      ],
    },
    {
      label: "navigation.plans",
      icon: "i-material-symbols-apartment",
      children: [
        {
          label: "navigation.actionpoints",
          to: useDvFlagAccess("beepiz-action-point-can:read")
            ? "actionpoints"
            : "actionpoints_denied",
        },
        {
          label: "navigation.plans_manager",
          to: useDvFlagAccess("beepiz-manage-plan-can:read")
            ? "plans_manager"
            : "plans_manager_denied",
        },
      ],
    },
  ];

  // Conditionally add flagged features
  // Use try-catch to handle cases where flags aren't loaded yet
  try {
    const hazardFlag = useDvFlag("beepiz-hazard-reporting-can:read");
    if (flagService.flags !== null && (hazardFlag.access || !!hazardFlag.fallback)) {
      // Initialize hazard reporting service if not already done
      if (!hazardReportingService.initialized) {
        hazardReportingService.initialize();
      }

      baseLinks.push({
        label: "navigation.hazard_reporting",
        icon: "i-material-symbols-feedback",
        to: "/redirect/hazard-reporting",
        badge: hazardReportingService.hazardCount,
      });
    }

    const alertHistoryFlag = useDvFlag("beepiz-alert-history-can:read");
    if (flagService.flags !== null && (alertHistoryFlag.access || !!alertHistoryFlag.fallback)) {
      baseLinks[4].children.unshift({
        label: "navigation.alerts",
        to: useDvFlagAccess("beepiz-alert-history-can:read")
          ? "/redirect/alerts"
          : "alert_history_denied",
        badge: alertHistoryFlag.status ? String(alertHistoryFlag.status).charAt(0).toUpperCase() + String(alertHistoryFlag.status).slice(1) : undefined,
      })
    }

    // EPI nouveau module : entrée ajoutée à côté de l'EPI legacy quand le client a le flag ppe-v2.
    // Pointe vers /redirect/ppes (redirige vers le nouveau portail Devant).
    const ppeV2Flag = useDvFlag("beepiz-ppe-v2-can:read");
    if (flagService.flags !== null && useDvFlagAccess("beepiz-ppe-v2-can:read")) {
      // baseLinks[5] = groupe "parameters" ; insertion juste après l'entrée EPI legacy
      baseLinks[5].children.splice(4, 0, {
        label: "navigation.ppes",
        to: "/redirect/ppes",
        badge: ppeV2Flag.status ? String(ppeV2Flag.status).charAt(0).toUpperCase() + String(ppeV2Flag.status).slice(1) : undefined,
      });

      // Si badge, 2 entrées, sinon, on supprime l'entrée legacy
      if (!ppeV2Flag.status) {
        baseLinks[5].children.splice(3, 1);
      }
    }
  } catch (error) {
    console.log(error);
  }

  return baseLinks;
}

/**
 * Reactive function that checks if flags are loaded and returns updated links
 * @returns {Array} Array of navigation link objects that updates when flags change
 */
export function getReactiveNavbarLinks() {
  // Always get fresh links to ensure they include any newly loaded flags
  return getNavbarLinks();
}

/**
 * Check if flags are loaded - reactive function
 * @returns {boolean} True if flags have been loaded
 */
export function areFlagsLoaded() {
  // Access the reactive state directly to ensure Vue can track dependencies
  return flagService.flags !== null && !flagService.isLoading;
}

// For backward compatibility - but note this is static and won't update when flags load
export const navbarLinks = getNavbarLinks();
